require('./ctxBarChart.style.scss');

/*
 * Usage example
 * <ctx-bar-chart></ctx-bar-chart>
 *
 * Custom attributes:
 * - values - data in format of an array of objects with 'value' and 'label' attributes, example [ {value: 32, label: 'firstLabel' } ]
 * - colors - an array of hex color codes for bar rectangles
 * - height - set fixed height of bar chart (optional)
 * - width - set fixed width of bar chart (optional; if provided chart will not resize with window)
 * - showTooltip - show value of bar when mouseover (optional)
 * - valueUnit - text to be displayed alongside value number (optional)
 * - showValues - show values of bar to the right of bar chart (optional)
 */

const barHeight = 20;
const chartPadding = 35;
const labelRightPadding = 10;
const colors = [],
    margin = 40;

class BarChartController {
    constructor($scope, $element, $window, $timeout) {
        this.$element = $element;
        this.$timeout = $timeout;
        this.$scope = $scope;
        this.$window = $window;
    }

    $onInit() {
        if (!this.height) {
            this.height = (chartPadding * this.values.length) + (margin / 2);
        }
        this.colors = this.colors || [];
        this.$timeout(() => {
            this.draw();
            this.$scope.$watch(() => this.values, (newVal, oldVal) => { if (!_.isEqual(newVal, oldVal)) { this.update(); } });
        }, this.delay ? this.delay : 0);
        if (this.responsiveWidth) {
            this.$scope.$watch(() => this.$element.find('.barchart-content').width(), (width) => {
                if (!this.svg) return;
                this.svg.attr('width', width);
                this.x.range([0, width - (margin * 2) - this.labelWidth]);
                this.bar.selectAll('rect').attr('width', d => this.x(d.value) > 2 || !this.markSmallValue ? this.x(d.value) : 2)
            });
        }
    }


    draw() {
        let ctrl = this, d3 = this.$window.d3, labelWidth = 0, barWidth = 0;
        const contentChart = this.$element.find('.barchart-content'),
            placeholder = contentChart[0],
            height = this.height || contentChart.height(),
            width = this.width || contentChart.width(),
            unit = this.valueUnit || '',
            data = this.values.sort(this.sortByProperty('-value')),
            max = d3.max(data, d => d.value);

        // create y-axis
        const y = this.y = d3.scale.linear()
            .domain([0, data.length])
            .range([0, height]);

        // create new svg
        this.svg = d3.select(placeholder)
            .append('svg');

        if (!this.responsiveWidth) {
            this.svg.attr('viewBox', `0, 0, ${width}, ${height}`)
                .attr('preserveAspectRatio', 'none meet');
        }

        // set svg sizes
        this.svg.attr('width', this.width ? width : '100%');
        if (this.height) {
            this.svg.attr('height', height);
        }

        // create bars with data
        this.bar = this.svg.selectAll('.bar')
            .data(data)
            .enter()
            .append('g')
            .attr('class', 'bar');

        // move bars
        this.bar.attr('transform', (d, i) => `translate(${margin}, ${(i * ((height - chartPadding - barHeight) / (data.length > 1 ? data.length - 1 : 1))) + (chartPadding / 2)})`);

        // axis-y labels
        this.textLabel = this.bar.append('text')
            .attr('class', 'bar-label')
            .style('font-size', '12px')
            .attr('title', d => d.label)
            .attr('y', barHeight / 2)
            .attr('dy', '.33em') // vertical align middle
            .text(d => d.label)
            .each(function () {
                labelWidth = Math.ceil(Math.max(labelWidth, this.getBBox().width));
                labelWidth = labelWidth > 5000 ? 50 : labelWidth;
            })
            .attr('x', function () {
                var xValue = labelWidth - this.getBBox().width;
                return (xValue > 0 ? xValue : 0) - labelRightPadding;
            });

        // create x-axis
        const x = this.x = d3.scale.linear()
            .domain([0, max])
            .range([0, width - (margin * 2) - labelWidth]);

        this.labelWidth = labelWidth;
        this.textLabel.each(wrap);

        // create y-grid
        if (this.showGrid && this.showGrid !== 'false') {
            this.yGrid = this.svg.insert('g', ':first-child')
                .attr('class', 'grid')
                .attr('transform', `translate(${margin + labelWidth}, ${height - 15})`)
                .call(d3.svg.axis()
                    .scale(x)
                    .orient('bottom')
                    .ticks(6)
                    .tickSize(-height, 0, 0)
                );
        }

        // display x-axis
        this.xAxis = this.svg.append('g')
            .attr('class', 'axis axis--x')
            .attr('transform', `translate(${margin + labelWidth}, ${height - 13})`);

        if (!this.hideXaxis) {
            this.xAxis.call(d3.svg.axis()
                .scale(x)
                .orient('bottom')
                .ticks(6)
                .tickSize(1)
            );
        }

        // style x-axis baseline
        this.xAxis.selectAll('path')
            .attr('stroke-width', 1)
            .attr('fill', 'none')
            .attr('stroke', 'none');

        // create rect on bar
        this.bar.append('rect')
            .attr('transform', `translate(${labelWidth}, 0)`)
            .style('fill', (d, i) => this.colors[i])
            .attr('height', barHeight)
            .attr('width', d => x(d.value) > 2 || !this.markSmallValue ? x(d.value) : 2)
            .each(function () {
                barWidth = Math.ceil(Math.max(barWidth, this.getBBox().width));
            });

        // show values
        if (this.showValues && this.showValues !== 'false') {
            this.text = this.bar.append('text')
                .attr('y', barHeight / 2)
                .attr('dy', '.35em') // vertical align middle
                .attr('text-anchor', 'end')
                .style('font-size', '12px')
                .text(d => !d.title ? ((d.value) + unit) : ((d.title.value) + ' ' + d.title.unit.toUpperCase()))
                .attr('x', d => barWidth + labelWidth + margin);
        }

        if (this.showTooltip && this.showTooltip !== 'false') {
            const div = d3.select(placeholder).append('div').attr('class', 'toolTip'),
                divLabel = d3.select(placeholder).append('div').attr('class', 'toolTipDown'),
                thisWindow = this.$window;
            this.bar.selectAll('rect')
                .on('mousemove', function (d) {
                    const [cursorPositionLeft, cursorPositionTop] = thisWindow.d3.mouse(placeholder);
                    div.style('left', d => `${this.getBBox().width + labelWidth + margin + 5}px`);
                    div.style('top', d => `${cursorPositionTop - (barHeight / 2)}px`);
                    div.style('display', 'inline-block');
                    div.html(!d.title ? ((d.value) + unit) : ((d.title.value) + ' ' + d.title.unit.toUpperCase()));
                })
                .on('mouseout', d => div.style('display', 'none'));

            this.textLabel
                .on('mousemove', function (d) {
                    const [cursorPositionLeft, cursorPositionTop] = thisWindow.d3.mouse(placeholder);
                    divLabel.style('left', d => `${labelWidth + 5 - this.getBBox().width / 2}px`);
                    divLabel.style('top', d => `${cursorPositionTop + 5}px`);
                    divLabel.style('display', 'inline-block');
                    divLabel.html(d.label);
                })
                .on('mouseout', d => divLabel.style('display', 'none'));
        }

        function wrap() {
            var self = d3.select(this),
                textLength = self.node().getComputedTextLength(),
                text = self.text();
            while (textLength > labelWidth && text.length > 0) {
                text = text.slice(0, -1);
                self.text(text + '...');
                textLength = self.node().getComputedTextLength();
            }
        }
    }

    update() {
        const data = this.values,
            unit = this.valueUnit || '',
            x = this.x;

        const groups = this.svg.selectAll('g')
            .data(data);

        this.svg.selectAll('rect')
            .data(data)
            .transition()
            .duration(500)
            .attr('width', (d, i) => x(d.value) > 2 || !this.markSmallValue ? x(d.value) : 2);

        if (this.showValues && this.showValues !== 'false') {
            this.text
                .data(data)
                .text(d => !d.title ? ((d.value) + unit) : ((d.title.value) + ' ' + d.title.unit.toUpperCase()));
        }
    }

    sortByProperty(property) {
        var sortOrder = 1;
        if (property[0] === "-") {
            sortOrder = -1;
            property = property.substr(1);
        }
        return function (a, b) {
            var result = (a[property] < b[property]) ? -1 : (a[property] > b[property]) ? 1 : 0;
            return result * sortOrder;
        }
    }

}

const barChartComponent = {
    controller: BarChartController,
    scope: {},
    template: require('./ctxBarChart.template.html'),
    bindings: {
        values: '=',
        height: '<?',
        width: '<?',
        colors: '<',
        showTooltip: '<?',
        valueUnit: '@?',
        showValues: '@?',
        showGrid: '@?',
        chartTitle: '@?',
        delay: '<?',
        responsiveWidth: '<?',
        markSmallValue: '<?',
        hideXaxis: '<?'
    }
};

BarChartController.$inject = ['$scope', '$element', '$window', '$timeout'];

module.exports = barChartComponent;




// WEBPACK FOOTER //
// ./src/shared/ctxBarChart/ctxBarChart.component.js